import React, { useState, useEffect } from 'react';
import { Listbox } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { fetchRegions } from 'shared/stores/store';
import { formatPriceSummary } from 'shared/utils/currency';

function classNames(...classes) {
    return classes.filter(Boolean).join(' ');
}

const initializeOptions = (t) => ({
    propertyTypes: [
        { id: 1, value: 'villa', label: t('search.propertyTypes.options.villa') },
        { id: 2, value: 'apartment', label: t('search.propertyTypes.options.apartment') },
        { id: 3, value: 'townhouse', label: t('search.propertyTypes.options.townhouse') },
        { id: 4, value: 'penthouse', label: t('search.propertyTypes.options.penthouse') },
    ],
    roomOptions: [
        { id: 1, value: '0', label: t('search.rooms.options.zero') },
        { id: 2, value: '1', label: t('search.rooms.options.one') },
        { id: 3, value: '2', label: t('search.rooms.options.two') },
        { id: 4, value: '3', label: t('search.rooms.options.three') },
        { id: 5, value: '4', label: t('search.rooms.options.four') },
        { id: 6, value: '5', label: t('search.rooms.options.five') },
    ],
    priceOptions: [
        { id: 1, value: '100000', amount: 100000 },
        { id: 2, value: '200000', amount: 200000 },
        { id: 3, value: '300000', amount: 300000 },
        { id: 4, value: '500000', amount: 500000 },
        { id: 5, value: '750000', amount: 750000 },
        { id: 6, value: '1000000', amount: 1000000 },
        { id: 7, value: '1500000', amount: 1500000 },
        { id: 8, value: '2000000', amount: 2000000 },
    ]
});

export default function SearchForm({ onAdvancedSearch }) {
    const { t, i18n } = useTranslation('common');
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const regions = useSelector((state) => state.regions.list);
    const options = React.useMemo(() => initializeOptions(t), [t]); // Keep t dependency for other translations

    useEffect(() => {
        dispatch(fetchRegions());
    }, [dispatch]);

    const regionOptions = React.useMemo(() => {
        return regions
            .map((region) => ({
                id: region.id,
                value: region.name,
                label: region.name
            }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [regions]);

    const [selectedRegions, setSelectedRegions] = useState([]);
    const [selectedTypes, setSelectedTypes] = useState([]);
    const [selectedRooms, setSelectedRooms] = useState(null);
    const [minPrice, setMinPrice] = useState(null);
    const [maxPrice, setMaxPrice] = useState(null);

    const buildSearchParams = React.useCallback(() => {
        const params = new URLSearchParams();

        if (selectedRegions.length > 0) {
            selectedRegions.forEach((region, index) => {
                params.append(`estates[refinementList][parent_region_name][${index}]`, region.value);
            });
        }

        if (selectedTypes.length > 0) {
            selectedTypes.forEach((type, index) => {
                params.append(`estates[refinementList][type][${index}]`, type.value);
            });
        }

        if (selectedRooms) {
            params.append(`estates[refinementList][bedrooms][0]`, selectedRooms.value);
        }

        if (minPrice?.value || maxPrice?.value) {
            const priceRange = `${minPrice?.value || ''}:${maxPrice?.value || ''}`;
            params.append('estates[range][price]', priceRange);
        }

        return params;
    }, [selectedRegions, selectedTypes, selectedRooms, minPrice, maxPrice]);

    const handleSearch = (e) => {
        e.preventDefault();
        const params = buildSearchParams();
        navigate(`/listings?${params.toString()}`);
    };
    
    const handleAdvancedSearch = React.useCallback(() => {
        const params = buildSearchParams();
        params.append('advanced', '');
        navigate(`/listings?${params.toString()}`);
    }, [buildSearchParams, navigate]);
    
    // Expose function to parent component for advanced search
    React.useEffect(() => {
        if (onAdvancedSearch) {
            onAdvancedSearch(handleAdvancedSearch);
        }
    }, [onAdvancedSearch, handleAdvancedSearch]);

    return (
        <div className="w-full rounded-md border border-transparent bg-white p-4 mt-6 text-base font-medium text-gray-900">
            <form onSubmit={handleSearch}>
                <div>
                    <a href="/listings?map"
                       className="text-xs md:text-base block w-full text-center bg-sky-700 hover:bg-sky-800 text-white font-bold py-3 px-4 rounded focus:outline-none focus:shadow-outline"
                    >
                        {t('search.mapSearch.button')} <span aria-hidden="true">→</span>
                    </a>
                    <div className="relative mt-4 mb-4">
                        <div className="absolute inset-0 flex items-center" aria-hidden="true">
                            <div className="w-full border-t border-gray-200"></div>
                        </div>
                        <div className="relative flex justify-center text-xs md:text-sm/6 font-medium">
                            <span className="bg-white px-6 text-gray-900">{t('search.mapSearch.divider')}</span>
                        </div>
                    </div>
                </div>
                <div className="flex flex-wrap">
                    {/* Regions Dropdown */}
                    <div className="w-full md:w-12/12 mb-6 md:mb-4">
                        <Listbox value={selectedRegions} onChange={setSelectedRegions} multiple>
                            {({open}) => (
                                <div className="relative">
                                    <Listbox.Button className="text-xs relative w-full cursor-default rounded-md bg-white py-3 pl-3 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-sky-600 sm:text-sm sm:leading-6">
                                        <span className="block truncate text-left">
                                            {selectedRegions.length === 0
                                                ? t('search.location.placeholder')
                                                : selectedRegions.length === 1
                                                    ? selectedRegions[0].label
                                                    : t('search.location.multipleSelected', { count: selectedRegions.length })
                                            }
                                        </span>
                                        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true"/>
                                        </span>
                                    </Listbox.Button>

                                    <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                        {regionOptions.map((region) => (
                                            <Listbox.Option
                                                key={region.id}
                                                value={region}
                                                className={({active}) =>
                                                    classNames(
                                                        active ? 'bg-sky-600 text-white' : 'text-gray-900',
                                                        'relative cursor-default select-none py-2 pl-3 pr-9 text-xs md:text-base'
                                                    )
                                                }
                                            >
                                                {({selected, active}) => (
                                                    <>
                                                        <span className={classNames(selected ? 'font-semibold' : 'font-normal', 'block truncate')}>
                                                            {region.label}
                                                        </span>
                                                        {selected && (
                                                            <span className={classNames(
                                                                active ? 'text-white' : 'text-sky-600',
                                                                'absolute inset-y-0 right-0 flex items-center pr-4'
                                                            )}>
                                                                <CheckIcon className="h-5 w-5" aria-hidden="true"/>
                                                            </span>
                                                        )}
                                                    </>
                                                )}
                                            </Listbox.Option>
                                        ))}
                                    </Listbox.Options>
                                </div>
                            )}
                        </Listbox>
                    </div>

                    {/* Property Types Dropdown */}
                    <div className="w-6/12 mb-6 md:mb-4 pr-1.5">
                        <Listbox value={selectedTypes} onChange={setSelectedTypes} multiple>
                            {({open}) => (
                                <div className="relative">
                                    <Listbox.Button className="text-xs relative w-full cursor-default rounded-md bg-white py-3 pl-3 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-sky-600 sm:text-sm sm:leading-6">
                                        <span className="block truncate text-left">
                                            {selectedTypes.length === 0
                                                ? t('search.propertyTypes.placeholder')
                                                : selectedTypes.length === 1
                                                    ? selectedTypes[0].label
                                                    : t('search.propertyTypes.multipleSelected', { count: selectedTypes.length })
                                            }
                                        </span>
                                        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true"/>
                                        </span>
                                    </Listbox.Button>

                                    <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                        {options.propertyTypes.map((type) => (
                                            <Listbox.Option
                                                key={type.id}
                                                value={type}
                                                className={({active}) =>
                                                    classNames(
                                                        active ? 'bg-sky-600 text-white' : 'text-gray-900',
                                                        'relative cursor-default select-none py-2 pl-3 pr-9 text-xs md:text-base text-left'
                                                    )
                                                }
                                            >
                                                {({selected, active}) => (
                                                    <>
                                                        <span className={classNames(selected ? 'font-semibold' : 'font-normal', 'block truncate')}>
                                                            {type.label}
                                                        </span>
                                                        {selected && (
                                                            <span className={classNames(
                                                                active ? 'text-white' : 'text-sky-600',
                                                                'absolute inset-y-0 right-0 flex items-center pr-4'
                                                            )}>
                                                                <CheckIcon className="h-5 w-5" aria-hidden="true"/>
                                                            </span>
                                                        )}
                                                    </>
                                                )}
                                            </Listbox.Option>
                                        ))}
                                    </Listbox.Options>
                                </div>
                            )}
                        </Listbox>
                    </div>

                    {/* Rooms Dropdown */}
                    <div className="w-6/12 mb-6 md:mb-4 pl-1.5">
                        <Listbox value={selectedRooms} onChange={setSelectedRooms}>
                            {({open}) => (
                                <div className="relative">
                                    <Listbox.Button className="text-xs relative w-full cursor-default rounded-md bg-white py-3 pl-3 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-sky-600 sm:text-sm sm:leading-6">
                                        <span className="block truncate text-left">
                                            {selectedRooms ? selectedRooms.label : t('search.rooms.placeholder')}
                                        </span>
                                        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true"/>
                                        </span>
                                    </Listbox.Button>

                                    <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                        {options.roomOptions.map((option) => (
                                            <Listbox.Option
                                                key={option.id}
                                                value={option}
                                                className={({active}) =>
                                                    classNames(
                                                        active ? 'bg-sky-600 text-white' : 'text-gray-900',
                                                        'relative cursor-default select-none py-2 pl-3 pr-9 text-xs md:text-base text-left'
                                                    )
                                                }
                                            >
                                                {({selected, active}) => (
                                                    <>
                                                        <span className={classNames(selected ? 'font-semibold' : 'font-normal', 'block truncate')}>
                                                            {option.label}
                                                        </span>
                                                        {selected && (
                                                            <span className={classNames(
                                                                active ? 'text-white' : 'text-sky-600',
                                                                'absolute inset-y-0 right-0 flex items-center pr-4'
                                                            )}>
                                                                <CheckIcon className="h-5 w-5" aria-hidden="true"/>
                                                            </span>
                                                        )}
                                                    </>
                                                )}
                                            </Listbox.Option>
                                        ))}
                                    </Listbox.Options>
                                </div>
                            )}
                        </Listbox>
                    </div>

                    {/* Min Price Dropdown */}
                    <div className="w-6/12 mb-6 md:mb-4 pr-1.5">
                        <Listbox value={minPrice} onChange={setMinPrice}>
                            {({open}) => (
                                <div className="relative">
                                    <Listbox.Button
                                        className="text-xs relative w-full cursor-default rounded-md bg-white py-3 pl-3 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-sky-600 sm:text-sm sm:leading-6">
                                        <span className="block truncate text-left">
        {minPrice
            ? `${t('search.price.min.prefix')} ${formatPriceSummary(minPrice.amount)}`
            : t('search.price.min.placeholder')
        }
    </span>
                                        <span
                                            className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true"/>
                                        </span>
                                    </Listbox.Button>

                                    <Listbox.Options
                                        className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                        {options.priceOptions.map((price) => (
                                            <Listbox.Option
                                                key={price.id}
                                                value={price}
                                                className={({active}) =>
                                                    classNames(
                                                        active ? 'bg-sky-600 text-white' : 'text-gray-900',
                                                        'relative cursor-default select-none py-2 pl-3 pr-9 text-xs md:text-base'
                                                    )
                                                }
                                            >
                                                {({selected, active}) => (
                                                    <>
                                                        <div className="flex flex-col">
        <span className={classNames(selected ? 'font-semibold' : 'font-normal', 'block truncate')}>
            {formatPriceSummary(price.amount)}
        </span>
                                                        </div>
                                                        {selected && (
                                                            <span className={classNames(
                                                                active ? 'text-white' : 'text-sky-600',
                                                                'absolute inset-y-0 right-0 flex items-center pr-4'
                                                            )}>
                                                                <CheckIcon className="h-5 w-5" aria-hidden="true"/>
                                                            </span>
                                                        )}
                                                    </>
                                                )}
                                            </Listbox.Option>
                                        ))}
                                    </Listbox.Options>
                                </div>
                            )}
                        </Listbox>
                    </div>

                    {/* Max Price Dropdown */}
                    <div className="w-6/12 mb-6 md:mb-4 pl-1.5">
                        <Listbox value={maxPrice} onChange={setMaxPrice}>
                            {({open}) => (
                                <div className="relative">
                                    <Listbox.Button
                                        className="text-xs relative w-full cursor-default rounded-md bg-white py-3 pl-3 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-sky-600 sm:text-sm sm:leading-6">
                                       <span className="block truncate text-left">
        {maxPrice
            ? `${t('search.price.max.prefix')} ${formatPriceSummary(maxPrice.amount)}`
            : t('search.price.max.placeholder')
        }
    </span>
                                        <span
                                            className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true"/>
                                        </span>
                                    </Listbox.Button>

                                    <Listbox.Options
                                        className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                        {options.priceOptions.map((price) => (
                                            <Listbox.Option
                                                key={price.id}
                                                value={price}
                                                className={({active}) =>
                                                    classNames(
                                                        active ? 'bg-sky-600 text-white' : 'text-gray-900',
                                                        'relative cursor-default select-none py-2 pl-3 pr-9 text-xs md:text-base'
                                                    )
                                                }
                                            >
                                                {({selected, active}) => (
                                                    <>
                                                        <div className="flex flex-col">
        <span className={classNames(selected ? 'font-semibold' : 'font-normal', 'block truncate')}>
            {formatPriceSummary(price.amount)}
        </span>
                                                        </div>
                                                        {selected && (
                                                            <span className={classNames(
                                                                active ? 'text-white' : 'text-sky-600',
                                                                'absolute inset-y-0 right-0 flex items-center pr-4'
                                                            )}>
                                                                <CheckIcon className="h-5 w-5" aria-hidden="true"/>
                                                            </span>
                                                        )}
                                                    </>
                                                )}
                                            </Listbox.Option>
                                        ))}
                                    </Listbox.Options>
                                </div>
                            )}
                        </Listbox>
                    </div>

                    {/* Search Button */}
                    <div className="w-full md:w-12/12 mb-4 md:mb-0">
                        <button
                            type="submit"
                            className="w-full bg-sky-700 hover:bg-sky-800 text-white text-xs md:text-base font-bold py-3 px-4 rounded focus:outline-none focus:shadow-outline"
                        >
                            {t('search.buttons.search')} <span aria-hidden="true">→</span>
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
}